import { apiClient, getData, getPage } from '@/api/client'
import type { Meta } from '@/api/envelope'
import type { components } from '@/api/schema'
import {
  dividendSummaryWireSchema,
  panelWireSchema,
  type DividendSummaryWire,
  type LimitTestWire,
  type MetricReadingWire,
  type PanelColumnWire,
  type PanelWire,
} from '@/features/company/api/wire'
import type { FloorsheetPeriod } from '@/features/company/lib/floorsheetPeriod'
import { brokerDisplayName } from '@/schemas/broker'
import { blankToNull, knownWord } from '@/schemas/common'
import {
  companyFloorsheetSummarySchema,
  type CompanyFloorsheetSummary,
} from '@/schemas/company-floorsheet'
import {
  AGM_STATUSES,
  BOLLINGER_SIGNALS,
  EVENT_STATUSES,
  EVENT_TYPES,
  FINANCIAL_BLOCKS,
  LEAD_DIRECTIONS,
  MACD_SIGNALS,
  PANEL_AVAILABILITIES,
  REPORT_TYPES,
  SECTOR_VERDICTS,
  agmPageSchema,
  annualReportsSchema,
  announcementPageSchema,
  boardOfDirectorsSchema,
  companyComparisonSchema,
  companyDividendsSchema,
  companyEventsSchema,
  companyFinancialBlockSchema,
  companyIndicesSchema,
  companyKeyNumbersSchema,
  companyMetricsSchema,
  companyPanelSchema,
  companyReturnsSchema,
  companySentimentSchema,
  companyShareholdingSchema,
  companySwotSchema,
  companyTechnicalsSchema,
  companyVolatilitySchema,
  type AgmPage,
  type AnnouncementPage,
  type BoardMember,
  type CompanyComparison,
  type CompanyDividends,
  type CompanyEvents,
  type CompanyFinancialBlock,
  type CompanyIndices,
  type CompanyKeyNumbers,
  type CompanyMetrics,
  type CompanyPanel,
  type CompanyReturns,
  type CompanySentiment,
  type CompanyShareholding,
  type CompanySwot,
  type CompanyTechnicals,
  type CompanyVolatility,
  type CompareLeadField,
  type FinancialBlock,
  type MetricsQuery,
  type PanelQuery,
  type PanelTab,
  type ReportLink,
} from '@/schemas/company-profile'
import { FAVOURABLE_DIRECTIONS, JUDGEMENTS, LIMIT_COMPARATORS } from '@/schemas/reference'

/**
 * The Stock page's tab reads: `/api/v5/company/{symbol}/…` in, app types out.
 *
 * Structured like `features/mutual-funds/api/fundService.ts`: wire types come
 * from `@/api/schema` through `Wire<>`, never restated, so a regenerated
 * `schema.d.ts` that renames a field breaks the build HERE; every mapper's
 * result then goes through its Zod schema before it leaves the module. The
 * three shapes the OpenAPI gets wrong (panel, statement, dividend summary)
 * are parsed from `wire.ts` instead — see that file for why.
 *
 * Its own module beside `companyService.ts`, which keeps the reads still built
 * from market endpoints (the directory, the overview, the floorsheet, the
 * desk's news): those have other owners in this build, and these are one
 * family — every read here is one company, one endpoint, one schema.
 *
 * `symbol` is the page's upper-cased ticker, as StockScreen holds it. Not
 * normalised again here, so the cache key and the request can never name two
 * different companies.
 *
 * Every read is JWT-only (mm-be's `V5APIView`); an expired session is
 * refreshed and replayed by `getData`/`getPage` like any other read.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** A DRF decimal, or the data service's exact numeric text: "3796.12", "-0.45". */
const DECIMAL = /^-?\d+(\.\d+)?$/

/**
 * A figure the data service passes through as TEXT, as a decimal or null.
 *
 * Statement and metric values are the upstream's exact `numeric` rendered as
 * text, and a panel value is a float mm-be re-serialises — either can carry
 * `NaN`, which mm-be's own reference code treats as "not recorded". Mapped to
 * null here, so one odd cell reads as unreported instead of failing the
 * statement it sits in; logged, because it is still a data problem.
 */
const decimalOrNull = (value: string | null): string | null => {
  if (value === null) return null
  const trimmed = value.trim()
  if (DECIMAL.test(trimmed)) return trimmed
  console.warn('a company figure that is not a decimal was treated as not reported', value)
  return null
}

/** `082-083` (how the panel answers) or `082083` (how every read asks). */
const FISCAL_YEAR = /^\d{3}-?\d{3}$/

/**
 * A fiscal year in the six-digit form every company read TAKES, so a year
 * the panel served can be asked for again. Anything else is null rather than
 * a guess: stripping punctuation from a shape this does not know ("2082/83")
 * would yield six digits that name a different year.
 */
const fiscalYearCode = (value: string | null): string | null =>
  value !== null && FISCAL_YEAR.test(value.trim()) ? value.trim().replace('-', '') : null

/**
 * The sentiment index on the gauge's 0–100 track. Bounded rather than
 * trusted, as the market rail's own reading is (`railService.clampScore`): a
 * marker's position is computed straight from it.
 */
const clampScore = (value: string): number => Math.min(Math.max(Number(value), 0), 100)

/**
 * One server page's position. `meta` carries mm-be's `PageMeta` (`page`,
 * `page_size` — clamped to 1–200 server-side — `total`, `total_pages`); the
 * asked values stand in only if a deployment ever omits it.
 */
const pagePosition = (meta: Meta, shown: number, page: number, pageSize: number) => {
  const total = meta?.total ?? shown
  const size = meta?.page_size ?? pageSize
  return {
    page: meta?.page ?? page,
    pageSize: size,
    total,
    totalPages: meta?.total_pages ?? Math.ceil(total / size),
  }
}

/**
 * `/dividends/`'s stat line, out of the list's free-form `meta`. Null when it
 * is absent or does not parse — the history still draws without it.
 */
const dividendSummaryOf = (meta: Meta): DividendSummaryWire | null => {
  if (meta === null || !('summary' in meta)) return null
  const parsed = dividendSummaryWireSchema.safeParse(meta.summary)
  if (parsed.success) return parsed.data
  console.warn('dropped an unparseable dividend summary', parsed.error, meta.summary)
  return null
}

/** `CompareCompany`'s field for each metric code mm-be ranks in `leads`. */
const COMPARE_LEAD_FIELD: Readonly<Record<string, CompareLeadField>> = {
  change_percent: 'changePercent',
  market_cap: 'marketCap',
  pe_ratio: 'peRatio',
  pb_ratio: 'pbRatio',
  eps: 'eps',
  book_value: 'bookValue',
  dividend_yield: 'dividendYield',
  roe: 'roe',
  npl: 'npl',
}

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each one is testable on a fixture without a request. They map
// only; the schema at the call site checks what they produce.

export const mapSentiment = (wire: Wire<'Sentiment'>) => ({
  date: wire.date,
  score: clampScore(wire.index),
  label: blankToNull(wire.verdict),
})

export const mapMetrics = (wire: Wire<'CompanyMetrics'>) => ({
  symbol: wire.symbol,
  periodId: wire.period_id,
  metrics: wire.metrics.map((metric) => ({
    code: metric.code,
    label: blankToNull(metric.label) ?? metric.code,
    quantityKind: blankToNull(metric.quantity_kind),
    value: decimalOrNull(metric.value),
    isProvisional: metric.is_provisional,
    supersededValue: decimalOrNull(metric.superseded_value),
  })),
})

const mapMetricReading = (wire: MetricReadingWire) => ({
  code: wire.code,
  favourable: knownWord(FAVOURABLE_DIRECTIONS, wire.favourable),
  reading: blankToNull(wire.reading),
  comparison: blankToNull(wire.comparison),
  judgement: knownWord(JUDGEMENTS, wire.judgement),
  explainer: blankToNull(wire.explainer),
})

/**
 * `rule` and `label` fall back to the code when blank. Both are free text in
 * mm-be's limits admin, and one blank would fail its column's parse, so the
 * whole card (CAR, say) would vanish over a missing caption. The code is a
 * true, if plainer, name for the limit, and a rule of its own to group on.
 */
const mapLimitTest = (wire: LimitTestWire) => ({
  limit: {
    code: wire.limit.code,
    rule: blankToNull(wire.limit.rule) ?? wire.limit.code,
    label: blankToNull(wire.limit.label) ?? wire.limit.code,
    value: wire.limit.value,
    unit: blankToNull(wire.limit.unit),
    comparator: knownWord(LIMIT_COMPARATORS, wire.limit.comparator),
    sector: blankToNull(wire.limit.sector),
    wordWithin: blankToNull(wire.limit.word_within),
    wordBreached: blankToNull(wire.limit.word_breached),
    explainer: blankToNull(wire.limit.explainer),
    source: blankToNull(wire.limit.source),
    effectiveFrom: wire.limit.effective_from,
  },
  within: wire.within,
  headroom: decimalOrNull(wire.headroom),
  word: blankToNull(wire.word),
})

/**
 * The column's sector context, or null. All three figures or none: a range
 * bar with an unreadable end would place the company's marker wrongly.
 */
const mapPanelSector = (sector: NonNullable<PanelColumnWire['sector']>) => {
  const median = decimalOrNull(sector.median)
  const low = decimalOrNull(sector.low)
  const high = decimalOrNull(sector.high)
  if (median === null || low === null || high === null) return null
  return {
    median,
    low,
    high,
    members: sector.members,
    verdict: knownWord(SECTOR_VERDICTS, sector.verdict),
  }
}

export const mapPanel = (wire: PanelWire) => ({
  symbol: wire.symbol,
  tab: wire.tab,
  slug: wire.slug,
  revision: wire.revision,
  year: fiscalYearCode(wire.year),
  period: blankToNull(wire.period)?.toLowerCase() ?? null,
  availability: knownWord(PANEL_AVAILABILITIES, wire.availability),
  availableTabs: wire.available_tabs,
  columns: wire.columns.map((column) => ({
    key: column.key,
    label: blankToNull(column.label) ?? column.key,
    unit: blankToNull(column.unit),
    value: decimalOrNull(column.value),
    sector: column.sector === null ? null : mapPanelSector(column.sector),
    reading: column.reading === null ? null : mapMetricReading(column.reading),
    regulatory: column.regulatory === null ? null : mapLimitTest(column.regulatory),
  })),
  regulatory: wire.regulatory.map(mapLimitTest),
  isSample: wire.is_sample,
})

/** A Financials block period's year and quarter, in the forms every read takes. */
const mapBlockPeriod = (wire: Wire<'BlockPeriod'>) => ({
  year: fiscalYearCode(wire.year),
  period: blankToNull(wire.period)?.toLowerCase() ?? null,
  availability: knownWord(PANEL_AVAILABILITIES, wire.availability),
})

/** One feed column with its year-earlier figure and its sector context. */
const mapBlockRow = (row: Wire<'BlockRow'>) => ({
  key: row.key,
  label: blankToNull(row.label) ?? row.key,
  unit: blankToNull(row.unit),
  value: decimalOrNull(row.value),
  previous: decimalOrNull(row.previous),
  changePercent: decimalOrNull(row.change_percent),
  sector: row.sector === null ? null : mapPanelSector(row.sector),
})

/**
 * One Financials block. Its rows are the panel's columns with a year-earlier
 * figure and a change beside them, so they read through the panel's own
 * helpers: a figure that is not a decimal is unreported, and the sector stats
 * come whole or not at all. A block code this build does not know is dropped
 * from `availableBlocks` rather than drawn as a section with no name.
 */
export const mapFinancialBlock = (wire: Wire<'FinancialBlock'>) => {
  const current = mapBlockPeriod(wire)
  return {
    symbol: wire.symbol,
    block: wire.block,
    year: current.year,
    period: current.period,
    availability: current.availability,
    availableBlocks: wire.available_blocks.flatMap((block) => {
      const known = knownWord(FINANCIAL_BLOCKS, block)
      return known === null ? [] : [known]
    }),
    periods: wire.periods.map(mapBlockPeriod),
    rows: wire.rows.map(mapBlockRow),
  }
}

/**
 * Overview's Key numbers. Its rows are a Financials block's (one helper, so a
 * row reads the same on both tabs), and each derived number keeps mm-be's
 * `basis` sentence beside its figure.
 */
export const mapKeyNumbers = (wire: Wire<'KeyNumbers'>) => {
  const current = mapBlockPeriod(wire)
  return {
    symbol: wire.symbol,
    year: current.year,
    period: current.period,
    availability: current.availability,
    periods: wire.periods.map(mapBlockPeriod),
    rows: wire.rows.map(mapBlockRow),
    derived: wire.derived.map((number) => ({
      key: number.key,
      label: blankToNull(number.label) ?? number.key,
      unit: blankToNull(number.unit),
      value: decimalOrNull(number.value),
      basis: blankToNull(number.basis),
    })),
  }
}

const mapReturnSeries = (wire: Wire<'ReturnSeries'>) => ({
  symbol: wire.symbol,
  name: wire.name,
  lastClose: wire.last_close,
  lastCloseDate: wire.last_close_date,
})

export const mapReturns = (wire: Wire<'CompanyReturns'>) => ({
  symbol: wire.symbol,
  asOf: wire.as_of,
  company: mapReturnSeries(wire.company),
  sector: wire.sector === null ? null : mapReturnSeries(wire.sector),
  nepse: wire.nepse === null ? null : mapReturnSeries(wire.nepse),
  windows: wire.windows.map((window) => ({
    period: window.period,
    startDate: window.start_date,
    company: window.company_percent,
    sector: window.sector_percent,
    nepse: window.nepse_percent,
    vsSector: window.vs_sector_points,
    vsNepse: window.vs_nepse_points,
  })),
  priceCagr: wire.price_cagr.map((cagr) => ({
    years: cagr.years,
    startDate: cagr.start_date,
    startClose: cagr.start_close,
    percent: cagr.cagr_percent,
  })),
})

export const mapIndices = (wire: Wire<'CompanyIndexPresence'>) => ({
  symbol: wire.symbol,
  session: wire.session,
  indices: wire.indices.map((index) => ({
    key: index.key,
    name: index.name,
    value: index.value,
    changePercent: index.change_percent,
    weightPercent: index.market_cap_share_percent,
  })),
})

export const mapVolatility = (wire: Wire<'CompanyVolatility'>) => ({
  symbol: wire.symbol,
  windowSessions: wire.window_sessions,
  sessionsUsed: wire.sessions_used,
  dailyPercent: wire.daily_volatility_percent,
  nepsePercent: wire.nepse_daily_volatility_percent,
  ratio: wire.ratio_to_nepse,
  label: blankToNull(wire.label),
})

export const mapTechnicals = (wire: Wire<'TechnicalIndicators'>) => ({
  asOf: wire.as_of,
  rsi: wire.rsi,
  macd: wire.macd,
  macdSignal: wire.macd_signal,
  macdVsSignal: knownWord(MACD_SIGNALS, wire.macd_vs_signal),
  stochastic: wire.stochastic,
  adx: wire.adx,
  mfi: wire.mfi,
  sma20: wire.sma_20,
  sma50: wire.sma_50,
  sma200: wire.sma_200,
  ema8: wire.ema_8,
  ema13: wire.ema_13,
  ema20: wire.ema_20,
  bollingerUpper: wire.bollinger_upper,
  bollingerMiddle: wire.bollinger_middle,
  bollingerLower: wire.bollinger_lower,
  bollingerSignal: knownWord(BOLLINGER_SIGNALS, wire.bollinger_signal),
  vwap13: wire.vwap_13,
  thirtyDayAverage: wire.thirty_day_avg,
  weekBeta: wire.week_beta,
  monthBeta: wire.month_beta,
  quarterBeta: wire.quarter_beta,
  yearBeta: wire.year_beta,
  // `?? null`: an older row without the volume trio must not fail every technicals reader.
  volumeToday: wire.volume_today ?? null,
  volumeThirtyDayAverage: wire.volume_30_day_average ?? null,
  volumeVsAverage: wire.volume_vs_average ?? null,
})

export const mapDividend = (wire: Wire<'Dividend'>) => ({
  fiscalYear: blankToNull(wire.fiscal_year),
  cashPercent: wire.cash,
  bonusPercent: wire.bonus,
  totalPercent: wire.total_dividend,
  announcedDate: wire.announced_date,
  bookClosureDate: wire.book_closure_date,
  distributionDate: wire.distribution_date,
  bonusListingDate: wire.bonus_listing_date,
})

export const mapDividendSummary = (wire: DividendSummaryWire) => ({
  nextBookClosureDate: wire.next_book_closure_date,
  daysToBookClosure: wire.days_to_book_closure,
  windowYears: wire.window_years,
  yearsPaid: wire.years_paid,
  averageTotalPercent: wire.average_total_percent,
})

export const mapEvent = (wire: Wire<'CompanyEvent'>) => ({
  date: wire.date,
  type: knownWord(EVENT_TYPES, wire.type),
  title: wire.title,
  status: knownWord(EVENT_STATUSES, wire.status),
  subtitle: blankToNull(wire.subtitle),
})

export const mapAgm = (wire: Wire<'AGM'>) => ({
  label: blankToNull(wire.agm),
  date: wire.date,
  bookClosureDate: wire.book_closure_date,
  venue: blankToNull(wire.venue),
  agenda: blankToNull(wire.agenda),
  // An older row can lack the key although the spec requires it.
  time: blankToNull(wire.time ?? null),
  status: knownWord(AGM_STATUSES, wire.status),
})

export const mapAnnouncement = (wire: Wire<'Announcement'>) => ({
  date: wire.date,
  title: wire.title,
  type: blankToNull(wire.type),
  message: blankToNull(wire.message),
  description: blankToNull(wire.description),
})

export const mapShareholding = (wire: Wire<'CompanyShareholding'>) => ({
  promoterHolding: wire.promoter_holding,
  publicHolding: wire.public_holding,
  promoters: wire.promoters.map((promoter) => ({
    name: promoter.name,
    designation: blankToNull(promoter.designation),
    ownership: blankToNull(promoter.ownership),
  })),
  shareholders: wire.shareholders.map((holder) => ({
    name: holder.name,
    units: holder.units,
    holdingValue: holder.holding_value,
    percentage: holder.percentage,
  })),
})

export const mapBoardMember = (wire: Wire<'BoardMember'>) => ({
  name: wire.name,
  designation: blankToNull(wire.designation),
  ownership: blankToNull(wire.ownership),
  isChair: wire.is_chair,
})

/** mm-be already drops blank segments; filtered again so one cannot become an empty note. */
const notes = (items: readonly string[]): string[] =>
  items.map((item) => item.trim()).filter((item) => item !== '')

export const mapSwot = (wire: Wire<'Swot'>) => ({
  strengths: notes(wire.strength),
  weaknesses: notes(wire.weakness),
  opportunities: notes(wire.opportunities),
  threats: notes(wire.threats),
})

export const mapReportLink = (wire: Wire<'ReportLink'>) => ({
  name: wire.name,
  year: blankToNull(wire.year),
  link: wire.link,
  type: knownWord(REPORT_TYPES, wire.type),
})

/**
 * `asked` stands in for a null `symbol`: mm-be answers in the order it was
 * asked, so the index names the company even where the row does not.
 *
 * `thirty_day_average` is on the wire and deliberately NOT mapped: mm-be's
 * `CompareCompany` dataclass has no such field and `_row` never sets it, so
 * DRF serialises it as null for every company. A field that exists is a field
 * the next screen renders, and this one cannot hold a value.
 */
/**
 * Compare's Financials tab (net profit, deposits, loans, total assets, per company):
 * `/company/compare/` serves none of them yet (`asks/cp1.md` A8). Off, the field is not read and
 * the tab says "not live yet"; one constant to flip once `financials` ships.
 */
export const COMPARE_FINANCIALS_AVAILABLE = false as boolean

export const mapComparison = (wire: Wire<'Comparison'>, asked: readonly string[]) => ({
  companies: wire.companies.map((company, index) => {
    const symbol = blankToNull(company.symbol)?.toUpperCase() ?? asked[index] ?? null
    return {
      symbol,
      name: blankToNull(company.name) ?? symbol,
      sectorName: blankToNull(company.sector_name),
      lastPrice: company.last_price,
      changePercent: company.change_percent,
      marketCap: company.market_cap,
      fiftyTwoWeekHigh: company.fifty_two_week_high,
      fiftyTwoWeekLow: company.fifty_two_week_low,
      peRatio: company.pe_ratio,
      pbRatio: company.pb_ratio,
      eps: company.eps,
      bookValue: company.book_value,
      dividendYield: company.dividend_yield,
      roe: company.roe,
      npl: company.npl,
    }
  }),
  leads: wire.leads.map((lead) => ({
    metric: lead.metric,
    field: COMPARE_LEAD_FIELD[lead.metric] ?? null,
    label: lead.label,
    better: knownWord(LEAD_DIRECTIONS, lead.better),
    leader: blankToNull(lead.leader),
  })),
  // Proposed field (asks/cp1.md A8), not in the generated wire type yet.
  financials: COMPARE_FINANCIALS_AVAILABLE
    ? ((wire as { financials?: unknown }).financials ?? [])
    : [],
})

/**
 * The Floorsheet tab's summary and houses. `name` falls back to "Broker 07"
 * the way every broker surface does, so `BrokerCell` always has a label.
 */
export const mapCompanyFloorsheet = (wire: Wire<'CompanyFloorsheet'>) => ({
  fromDate: wire.from_date,
  toDate: wire.to_date,
  summary:
    wire.summary === null
      ? null
      : {
          trades: wire.summary.trades,
          kitta: wire.summary.kitta,
          value: wire.summary.value,
          averageTradeSize: wire.summary.average_trade_size,
          largestTradeKitta: wire.summary.largest_trade_quantity,
          tradesVsAveragePercent: wire.summary.trades_vs_30_day_average_percent,
          kittaVsNormalDay: wire.summary.kitta_vs_normal_day,
          turnoverRank: wire.summary.turnover_rank,
          rankedScrips: wire.summary.ranked_scrips,
        },
  brokers: wire.brokers.map((row) => ({
    code: row.code,
    name: brokerDisplayName(row.code, row.name),
    rating: row.rating,
    bought: row.bought,
    sold: row.sold,
    net: row.net,
  })),
  brokersTotal: wire.brokers_total,
})

/**
 * The panel's query string. The OpenAPI declares no query parameters for
 * this route (see `wire.ts`), so the typed `params.query` cannot carry them;
 * openapi-fetch hands the serializer the (empty) typed query and appends
 * whatever it returns.
 */
const panelQueryString = (query: PanelQuery): string =>
  new URLSearchParams({
    ...(query.year === undefined ? {} : { year: query.year }),
    ...(query.period === undefined ? {} : { period: query.period }),
  }).toString()

// ── The endpoints ────────────────────────────────────────────────────────────

export const companyProfileService = {
  /**
   * The company's fear-and-greed reading, or null when mm-be has none
   * recorded (`data: null` — the OpenAPI types it non-null, wrongly).
   */
  sentiment: async (symbol: string): Promise<CompanySentiment | null> => {
    const data = await getData<Wire<'Sentiment'> | null>(() =>
      apiClient.GET('/api/v5/company/{symbol}/sentiment/', { params: { path: { symbol } } }),
    )
    return data === null ? null : companySentimentSchema.parse(mapSentiment(data))
  },

  /** The company's computed metrics at one period, or its latest. Empty is normal. */
  metrics: async (symbol: string, query: MetricsQuery): Promise<CompanyMetrics> => {
    const data = await getData<Wire<'CompanyMetrics'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/metrics/', {
        params: {
          path: { symbol },
          query:
            query.period === undefined
              ? {}
              : { year: query.period.year, period: query.period.quarter },
        },
      }),
    )
    return companyMetricsSchema.parse(mapMetrics(data))
  },

  /**
   * One Advanced tab. Throws `404 PANEL_NOT_AVAILABLE_FOR_SECTOR` as it comes;
   * `companyPanelQueryOptions` turns that one code into a value.
   */
  panel: async (symbol: string, tab: PanelTab, query: PanelQuery): Promise<CompanyPanel> => {
    const raw = await getData<unknown>(() =>
      apiClient.GET('/api/v5/company/{symbol}/panel/{tab}/', {
        params: { path: { symbol, tab } },
        querySerializer: () => panelQueryString(query),
      }),
    )
    return companyPanelSchema.parse(mapPanel(panelWireSchema.parse(raw)))
  },

  /**
   * One Financials block, at one period or the company's latest. Throws `404
   * PANEL_NOT_AVAILABLE_FOR_SECTOR` as it comes, for a block the sector has no
   * feed for; `companyFinancialBlockQueryOptions` turns that into a value.
   */
  financialBlock: async (
    symbol: string,
    block: FinancialBlock,
    query: PanelQuery,
  ): Promise<CompanyFinancialBlock> => {
    const data = await getData<Wire<'FinancialBlock'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/financials/{block}/', {
        params: {
          // The path spells a block with hyphens, the response with underscores.
          path: { symbol, block: block.replaceAll('_', '-') },
          query: {
            ...(query.year === undefined ? {} : { year: query.year }),
            ...(query.period === undefined ? {} : { period: query.period }),
          },
        },
      }),
    )
    return companyFinancialBlockSchema.parse(mapFinancialBlock(data))
  },

  /**
   * Overview's Key numbers, always at the company's latest filed period.
   * Throws `404 PANEL_NOT_AVAILABLE_FOR_SECTOR` and `COMPANY_NOT_FOUND` as
   * they come; `companyKeyNumbersQueryOptions` turns both into a value.
   */
  keyNumbers: async (symbol: string): Promise<CompanyKeyNumbers> => {
    const data = await getData<Wire<'KeyNumbers'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/key-numbers/', { params: { path: { symbol } } }),
    )
    return companyKeyNumbersSchema.parse(mapKeyNumbers(data))
  },

  /** Price returns over 1W–3Y beside the sector index and NEPSE, and price CAGR. */
  returns: async (symbol: string): Promise<CompanyReturns> => {
    const data = await getData<Wire<'CompanyReturns'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/returns/', { params: { path: { symbol } } }),
    )
    return companyReturnsSchema.parse(mapReturns(data))
  },

  /** The indices the company is in, each one's latest line and the company's weight in it. */
  indices: async (symbol: string): Promise<CompanyIndices> => {
    const data = await getData<Wire<'CompanyIndexPresence'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/indices/', { params: { path: { symbol } } }),
    )
    return companyIndicesSchema.parse(mapIndices(data))
  },

  /** Daily volatility over the last 60 sessions, beside NEPSE's over the same sessions. */
  volatility: async (symbol: string): Promise<CompanyVolatility> => {
    const data = await getData<Wire<'CompanyVolatility'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/volatility/', { params: { path: { symbol } } }),
    )
    return companyVolatilitySchema.parse(mapVolatility(data))
  },

  /**
   * The precomputed indicators, or null when the cron has not computed any
   * for this company (`data: null` — the OpenAPI types it non-null, wrongly).
   */
  technicals: async (symbol: string): Promise<CompanyTechnicals | null> => {
    const data = await getData<Wire<'TechnicalIndicators'> | null>(() =>
      apiClient.GET('/api/v5/company/{symbol}/technicals/', { params: { path: { symbol } } }),
    )
    return data === null ? null : companyTechnicalsSchema.parse(mapTechnicals(data))
  },

  /** The dividend history, newest first, and the stat line from its `meta.summary`. */
  dividends: async (symbol: string): Promise<CompanyDividends> => {
    const { rows, meta } = await getPage<Wire<'Dividend'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/dividends/', { params: { path: { symbol } } }),
    )
    const summary = dividendSummaryOf(meta)
    return companyDividendsSchema.parse({
      rows: rows.map(mapDividend),
      summary: summary === null ? null : mapDividendSummary(summary),
    })
  },

  /** The corporate-action calendar: upcoming (and active) events, then past ones. */
  events: async (symbol: string): Promise<CompanyEvents> => {
    const data = await getData<Wire<'CompanyEvents'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/events/', { params: { path: { symbol } } }),
    )
    return companyEventsSchema.parse({
      upcoming: data.upcoming.map(mapEvent),
      past: data.past.map(mapEvent),
    })
  },

  /** One page of AGMs, newest first. `page` is 1-based. */
  agms: async (symbol: string, page: number, pageSize: number): Promise<AgmPage> => {
    const { rows, meta } = await getPage<Wire<'AGM'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/agms/', {
        params: { path: { symbol }, query: { page, page_size: pageSize } },
      }),
    )
    return agmPageSchema.parse({
      rows: rows.map(mapAgm),
      ...pagePosition(meta, rows.length, page, pageSize),
    })
  },

  /** One page of dated notices, newest first. `page` is 1-based. */
  announcements: async (
    symbol: string,
    page: number,
    pageSize: number,
  ): Promise<AnnouncementPage> => {
    const { rows, meta } = await getPage<Wire<'Announcement'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/announcements/', {
        params: { path: { symbol }, query: { page, page_size: pageSize } },
      }),
    )
    return announcementPageSchema.parse({
      rows: rows.map(mapAnnouncement),
      ...pagePosition(meta, rows.length, page, pageSize),
    })
  },

  /**
   * The promoter/public split, the major promoters and the major INDIVIDUAL
   * shareholders — mm-be's default; `?institutional=true` swaps in the
   * institutions, which no screen draws yet.
   */
  shareholding: async (symbol: string): Promise<CompanyShareholding> => {
    const data = await getData<Wire<'CompanyShareholding'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/shareholding/', { params: { path: { symbol } } }),
    )
    return companyShareholdingSchema.parse(mapShareholding(data))
  },

  /** The board, chair first. An empty board is `[]`. */
  boardOfDirectors: async (symbol: string): Promise<BoardMember[]> => {
    const { rows } = await getPage<Wire<'BoardMember'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/board-of-directors/', {
        params: { path: { symbol } },
      }),
    )
    return boardOfDirectorsSchema.parse(rows.map(mapBoardMember))
  },

  /** The SWOT notes, or null when the company has no SWOT row (`data: null`). */
  swot: async (symbol: string): Promise<CompanySwot | null> => {
    const data = await getData<Wire<'Swot'> | null>(() =>
      apiClient.GET('/api/v5/company/{symbol}/swot/', { params: { path: { symbol } } }),
    )
    return data === null ? null : companySwotSchema.parse(mapSwot(data))
  },

  /** Annual report links, then quarterly ones, each newest first. */
  annualReports: async (symbol: string): Promise<ReportLink[]> => {
    const { rows } = await getPage<Wire<'ReportLink'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/annual-reports/', { params: { path: { symbol } } }),
    )
    return annualReportsSchema.parse(rows.map(mapReportLink))
  },

  /**
   * Two or three companies side by side. `symbols` arrives already distinct
   * and upper-cased, in column order (see `companyCompareQueryOptions`); the
   * array becomes the wire's comma-separated `symbols` here and nowhere else.
   */
  compare: async (symbols: readonly string[]): Promise<CompanyComparison> => {
    const data = await getData<Wire<'Comparison'>>(() =>
      apiClient.GET('/api/v5/company/compare/', {
        params: { query: { symbols: symbols.join(',') } },
      }),
    )
    return companyComparisonSchema.parse(mapComparison(data, symbols))
  },

  /**
   * One company's trading over a floorsheet window — the summary figures with
   * their comparisons, and the `brokers` busiest houses with both sides each.
   * mm-be caches the window for five minutes and cuts the house list after
   * the cache, so a different `brokers` costs it nothing.
   */
  floorsheet: async (
    symbol: string,
    period: FloorsheetPeriod,
    brokers: number,
  ): Promise<CompanyFloorsheetSummary> => {
    const data = await getData<Wire<'CompanyFloorsheet'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/floorsheet/', {
        params: { path: { symbol }, query: { period, brokers } },
      }),
    )
    return companyFloorsheetSummarySchema.parse(mapCompanyFloorsheet(data))
  },
}
